import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Tab, Nav, Form, Alert, ProgressBar } from 'react-bootstrap';
import useApi from '../../../hooks/useApi';
import { toast } from 'react-toastify';

const ShowAddModal = ({ showAddContactModal, setShowAddContactModal, activeTab, setActiveTab, onClose, isEdit = false, userToEdit = null }) => {
  const { addUser, updateUser, bulkCreateUsers, loading } = useApi();
  const [formData, setFormData] = useState({
    userName: '',
    emailId: '',
    phoneNumber: '',
    optedIn: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  // Pre-fill form when editing or reset for adding
  useEffect(() => {
    if (isEdit && userToEdit) {
      setFormData({
        userName: userToEdit.userName || '',
        emailId: userToEdit.emailId || '',
        phoneNumber: userToEdit.phoneNumber || '',
        optedIn: userToEdit.optedIn !== undefined ? userToEdit.optedIn : true,
      });
    } else {
      setFormData({
        userName: '',
        emailId: '',
        phoneNumber: '',
        optedIn: true,
      });
    }
  }, [isEdit, userToEdit, showAddContactModal]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      const validExtensions = ['csv', 'xlsx', 'xls', 'docx'];
      
      if (validExtensions.includes(fileExt)) {
        setFile(selectedFile);
        setFileError('');
      } else {
        setFileError('Please upload a CSV, Excel, or DOCX file');
        setFile(null);
      }
    }
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.userName.trim()) errors.userName = 'Name is required';
    if (!formData.emailId) {
      errors.emailId = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      errors.emailId = 'Invalid email format';
    }
    if (!formData.phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?\d{10,15}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Invalid phone number (10-15 digits)';
    }
    return errors;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const errors = validateForm();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
      
      try {
        if (isEdit && userToEdit) {
          await updateUser(userToEdit.userId, formData);
          toast.success('Contact updated successfully');
        } else {
          await addUser(formData);
          toast.success('Contact added successfully');
        }
        handleClose();
      } catch (err) {
        // Error is handled in useApi hook with toast
      }
    },
    [formData, validateForm, addUser, updateUser, isEdit, userToEdit]
  );

  const handleBulkUpload = useCallback(
    async (e) => {
      e.preventDefault();
      if (!file) {
        setFileError('Please select a file');
        return;
      }
      
      try {
        setUploadProgress(0);
        const progressCallback = (progress) => {
          setUploadProgress(Math.round(progress * 100));
        };
        
        const response = await bulkCreateUsers(file, progressCallback);
        toast.success(`Successfully imported ${response.count} contacts`);
        handleClose();
      } catch (err) {
        // Error is handled in useApi hook with toast
      } finally {
        setUploadProgress(0);
      }
    },
    [file, bulkCreateUsers]
  );

  const handleClose = () => {
    setFormData({ userName: '', emailId: '', phoneNumber: '', optedIn: true });
    setFormErrors({});
    setFile(null);
    setFileError('');
    setUploadProgress(0);
    setShowAddContactModal(false);
    if (onClose) onClose();
  };

  return (
    <Modal
      show={showAddContactModal}
      onHide={handleClose}
      centered
      size="lg"
    >
      <Modal.Header className="bg-success text-white">
        <Modal.Title>
          <i className={`bi bi-${isEdit ? 'person-check' : 'person-plus'} me-2`}></i>
          {isEdit ? 'Edit Contact' : 'Add New Contact'}
        </Modal.Title>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={handleClose}
          aria-label="Close"
        />
      </Modal.Header>
      <Modal.Body className="p-4">
        <Tab.Container activeKey={activeTab} onSelect={(tab) => setActiveTab(tab)}>
          <Nav variant="tabs" className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="single" className="d-flex align-items-center">
                <i className="bi bi-person me-2"></i>
                Single Contact
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="bulk" className="d-flex align-items-center">
                <i className="bi bi-upload me-2"></i>
                Bulk Upload
              </Nav.Link>
            </Nav.Item>
          </Nav>
          
          <Tab.Content>
            {/* Single Contact Form */}
            <Tab.Pane eventKey="single">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="userName"
                    value={formData.userName}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    isInvalid={!!formErrors.userName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.userName}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="emailId"
                        value={formData.emailId}
                        onChange={handleInputChange}
                        placeholder="example@domain.com"
                        isInvalid={!!formErrors.emailId}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.emailId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="+1234567890"
                        isInvalid={!!formErrors.phoneNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.phoneNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </div>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="switch"
                    id="optedInSwitch"
                    label="User has opted in to receive messages"
                    name="optedIn"
                    checked={formData.optedIn}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="outline-secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {isEdit ? 'Updating...' : 'Adding...'}
                      </>
                    ) : (
                      <>
                        <i className={`bi bi-${isEdit ? 'check-circle' : 'plus-circle'} me-2`}></i>
                        {isEdit ? 'Update Contact' : 'Add Contact'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Tab.Pane>

            {/* Bulk Upload Section */}
            <Tab.Pane eventKey="bulk">
              <Form onSubmit={handleBulkUpload}>
                <Alert variant="info" className="mb-4">
                  <i className="bi bi-info-circle me-2"></i>
                  Upload a CSV, Excel, or Word file containing your contacts. Ensure the file includes columns for name, email, and phone number.
                </Alert>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">Select File</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".csv,.xlsx,.xls,.docx"
                    onChange={handleFileChange}
                    isInvalid={!!fileError}
                  />
                  <Form.Text className="text-muted">
                    Maximum file size: 5MB
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {fileError}
                  </Form.Control.Feedback>
                </Form.Group>

                {uploadProgress > 0 && (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <small>Upload Progress</small>
                      <small>{uploadProgress}%</small>
                    </div>
                    <ProgressBar now={uploadProgress} animated striped />
                  </div>
                )}

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="outline-secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button 
                    variant="success" 
                    type="submit" 
                    disabled={loading || !file}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload me-2"></i>
                        Upload Contacts
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
    </Modal>
  );
};

export default ShowAddModal;